import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { useCandidate, useUpdateCandidateStatus, useSendCultureSurvey } from '../hooks/useCandidates';
import { clsx } from 'clsx';

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  culture_survey_sent: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  culture_survey_complete: 'bg-purple-100 text-purple-700 border-purple-200',
  interviewing: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  offer_sent: 'bg-perfectted-light text-perfectted-dark border-perfectted-primary',
  hired: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

const statusIcons: Record<string, React.ElementType> = {
  new: User,
  culture_survey_sent: Send,
  culture_survey_complete: FileText,
  interviewing: MessageSquare,
  offer_sent: CheckCircle,
  hired: CheckCircle,
  rejected: XCircle,
};

interface ActivityLogEntry {
  id: string;
  timestamp: string;
  action: string;
  details: string;
  user?: string;
}

export default function HiringDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showActivityLog, setShowActivityLog] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [newNote, setNewNote] = useState('');

  const { data: candidate, isLoading, refetch } = useCandidate(id!);
  const updateStatus = useUpdateCandidateStatus();
  const sendSurvey = useSendCultureSurvey();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-perfectted-primary" />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Candidate not found</h2>
        <p className="text-gray-500 mt-2">The candidate you're looking for doesn't exist.</p>
        <Link to="/hiring" className="text-perfectted-primary hover:text-perfectted-dark mt-4 inline-block">
          Back to Hiring
        </Link>
      </div>
    );
  }

  const StatusIcon = statusIcons[candidate.status] || User;

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleSendSurvey = async () => {
    await sendSurvey.mutateAsync(candidate.id);
    refetch();
  };

  const handleMoveToInterview = async () => {
    await updateStatus.mutateAsync({ id: candidate.id, status: 'interviewing' });
    refetch();
  };

  const handleSendOffer = async () => {
    await updateStatus.mutateAsync({ id: candidate.id, status: 'offer_sent' });
    refetch();
  };

  const handleHire = async () => {
    await updateStatus.mutateAsync({ id: candidate.id, status: 'hired' });
    refetch();
  };

  const handleReject = async () => {
    await updateStatus.mutateAsync({ id: candidate.id, status: 'rejected', reason: rejectReason });
    setShowRejectModal(false);
    refetch();
  };

  // Activity log from real data (or empty if none)
  const activityLog: ActivityLogEntry[] = candidate.activityLog || [];

  // Survey results from real data (or null if none)
  const surveyResults = candidate.surveyResults || null;

  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/hiring')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-perfectted-light flex items-center justify-center">
              <User className="w-8 h-8 text-perfectted-dark" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{candidate.name}</h2>
                <span className={clsx('px-3 py-1 rounded-full text-sm font-medium border', statusColors[candidate.status])}>
                  <StatusIcon className="w-4 h-4 inline mr-1.5" />
                  {formatStatus(candidate.status)}
                </span>
              </div>
              <p className="text-gray-500 mt-1 capitalize">{candidate.role} Position</p>
            </div>
          </div>
        </div>

        {/* Action buttons based on status */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowRejectModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <XCircle className="w-4 h-4" />
            Reject
          </button>

          {candidate.status === 'new' && (
            <button
              onClick={handleSendSurvey}
              disabled={sendSurvey.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              Send Culture Survey
            </button>
          )}

          {candidate.status === 'culture_survey_complete' && (
            <button
              onClick={handleMoveToInterview}
              disabled={updateStatus.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
            >
              <MessageSquare className="w-4 h-4" />
              Schedule Interview
            </button>
          )}

          {candidate.status === 'interviewing' && (
            <button
              onClick={handleSendOffer}
              disabled={updateStatus.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
            >
              <FileText className="w-4 h-4" />
              Send Offer
            </button>
          )}

          {candidate.status === 'offer_sent' && (
            <button
              onClick={handleHire}
              disabled={updateStatus.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Mark as Hired
            </button>
          )}
        </div>
      </div>

      {/* Candidate info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900 truncate">{candidate.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Phone className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium text-gray-900">{candidate.phone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Briefcase className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Role</p>
              <p className="font-medium text-gray-900 capitalize">{candidate.role}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Applied</p>
              <p className="font-medium text-gray-900">
                {format(new Date(candidate.appliedDate), 'dd MMM yyyy')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Culture Survey Results */}
          {candidate.surveyStatus === 'completed' && surveyResults && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Culture Survey Results</h3>

              {/* Overall score */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 rounded-full bg-perfectted-light flex items-center justify-center">
                  <span className="text-2xl font-bold text-perfectted-dark">{surveyResults.score}%</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Overall Culture Fit Score</p>
                  <p className="text-sm text-gray-500">Based on {surveyResults.categories?.length || 0} categories</p>
                </div>
              </div>

              {/* Category breakdown */}
              {surveyResults.categories && surveyResults.categories.length > 0 && (
                <div className="space-y-4">
                  {surveyResults.categories.map((category: { name: string; score: number }) => (
                    <div key={category.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{category.name}</span>
                        <span className="text-sm text-gray-500">{category.score}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-perfectted-primary h-2 rounded-full transition-all"
                          style={{ width: `${category.score}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Survey completed but no results data */}
          {candidate.surveyStatus === 'completed' && !surveyResults && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center text-gray-500">
              Survey completed but results data not available
            </div>
          )}

          {/* Survey pending */}
          {candidate.surveyStatus === 'sent' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-yellow-800">Culture Survey Pending</h4>
                  <p className="text-yellow-600 mt-1">
                    The culture survey has been sent to the candidate. Waiting for completion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Resume/Application */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Application Documents</h3>
            </div>
            <div className="space-y-3">
              {candidate.resume && (
                <a
                  href={candidate.resume}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-5 h-5 text-perfectted-primary" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Resume</p>
                    <p className="text-sm text-gray-500">PDF Document</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </a>
              )}
              {candidate.coverLetter && (
                <a
                  href={candidate.coverLetter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-5 h-5 text-perfectted-primary" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Cover Letter</p>
                    <p className="text-sm text-gray-500">PDF Document</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </a>
              )}
              {!candidate.resume && !candidate.coverLetter && (
                <p className="text-gray-500 text-center py-4">No documents attached</p>
              )}
            </div>
          </div>

          {/* Notes section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
              </div>
              {showNotes ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {showNotes && (
              <div className="px-6 pb-6">
                <div className="space-y-4 mb-4">
                  {candidate.notes?.map((note, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{note.author}</span>
                        <span className="text-sm text-gray-500">
                          {format(new Date(note.timestamp), 'dd MMM yyyy HH:mm')}
                        </span>
                      </div>
                      <p className="text-gray-600">{note.content}</p>
                    </div>
                  )) || (
                    <p className="text-gray-500 text-center py-4">No notes yet</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
                  />
                  <button
                    disabled={!newNote.trim()}
                    className="px-4 py-2 bg-perfectted-primary text-white rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Activity log sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Log</h3>
            <div className="space-y-4">
              {activityLog.map((entry, index) => (
                <div key={entry.id} className="relative pl-6">
                  {/* Timeline line */}
                  {index < activityLog.length - 1 && (
                    <div className="absolute left-[7px] top-6 w-0.5 h-full bg-gray-200" />
                  )}
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-perfectted-light border-2 border-perfectted-primary" />

                  <div>
                    <p className="font-medium text-gray-900">{entry.action}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{entry.details}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                      <span>{format(new Date(entry.timestamp), 'dd MMM yyyy HH:mm')}</span>
                      {entry.user && (
                        <>
                          <span>-</span>
                          <span>{entry.user}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Source info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Source</h3>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <ExternalLink className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{candidate.source || 'Direct Application'}</p>
                <p className="text-sm text-gray-500">Application source</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Reject Candidate</h3>
            <p className="text-gray-500 mt-2">
              Are you sure you want to reject this candidate? Please provide a reason.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full mt-4 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || updateStatus.isPending}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                Reject Candidate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
